<template>
    <div class="flex align-items-center justify-content-center min-h-screen p-4 bg-gray-50">
        <div class="card p-6 shadow-2 border-round-xl text-center surface-card" style="max-width: 450px; width: 100%">
            <div class="flex align-items-center justify-content-center mb-4">
                <div class="bg-red-100 border-circle flex align-items-center justify-content-center"
                    style="width: 80px; height: 80px">
                    <i class="pi pi-times text-4xl text-red-600"></i>
                </div>
            </div>

            <!-- Settings subscription: payment cancelled -->
            <template v-if="isSettingsReturn">
                <h1 class="text-2xl font-bold text-900 mb-2">Payment Cancelled</h1>
                <p class="text-600 mb-3 line-height-3">
                    Your payment was not processed. No changes were made to your subscription.
                </p>
                <div class="flex flex-column gap-3">
                    <Button label="Return to Settings" icon="pi pi-arrow-left text-white" class="p-button-raised try-again-btn" @click="returnToOnboarding" />
                </div>
            </template>

            <!-- Onboarding: payment cancelled -->
            <template v-else-if="isOnboardingReturn">
                <h1 class="text-2xl font-bold text-900 mb-2">Payment Cancelled</h1>
                <p v-if="isFreePlan" class="text-600 mb-3 line-height-3">
                    Your card verification was cancelled. No charge has been applied — you can try again by selecting a plan.
                </p>
                <p v-else class="text-600 mb-3 line-height-3">
                    Your payment was cancelled. You can try again by selecting a plan.
                </p>
                <div class="flex flex-column gap-3">
                    <Button label="Try Again" icon="pi pi-arrow-left text-white" class="p-button-raised try-again-btn" @click="returnToOnboarding" />
                </div>
            </template>

            <template v-else>
                <h1 class="text-2xl font-bold text-900 mb-2">Payment Failed</h1>
                <p class="text-600 mb-5 line-height-3 text-justify">
                    We couldn't process your transaction. This could be due to insufficient funds, an expired card, or a
                    temporary connection issue with Payment Gateway.
                </p>
                <div class="flex flex-column gap-3">
                    <Button label="Go to Dashboard" icon="pi pi-home" class="p-button-raised" @click="goDashboard" />
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import Button from 'primevue/button';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';
import subscriptionApi from '@/api/subscription';

const STUDENT_CARD_TOKENIZE_KEY           = 'studentCardTokenizePending';
const TUTOR_CARD_TOKENIZE_KEY             = 'tutorCardTokenizePending';
const INSTITUTE_CARD_TOKENIZE_KEY         = 'instituteCardTokenizePending';
const TUTOR_PENDING_KEY                   = 'tutorOnboardingPendingPackage';
const STUDENT_PENDING_KEY                 = 'studentOnboardingPendingPackage';
const INSTITUTE_PENDING_KEY               = 'instituteOnboardingPendingPackage';
const TUTOR_SETTINGS_PENDING_KEY          = 'tutorSettingsPendingPackage';
const STUDENT_SETTINGS_PENDING_KEY        = 'studentSettingsPendingPackage';
const INSTITUTE_SETTINGS_PENDING_KEY      = 'instituteSettingsPendingPackage';
const TUTOR_FAILED_STATE_KEY              = 'tutorOnboardingFailedReturn';
const STUDENT_FAILED_STATE_KEY            = 'studentOnboardingFailedReturn';
const INSTITUTE_FAILED_STATE_KEY          = 'instituteOnboardingFailedReturn';
const TUTOR_SETTINGS_FAILED_STATE_KEY     = 'tutorSettingsFailedReturn';
const STUDENT_SETTINGS_FAILED_STATE_KEY   = 'studentSettingsFailedReturn';
const INSTITUTE_SETTINGS_FAILED_STATE_KEY = 'instituteSettingsFailedReturn';

const router    = useRouter();
const authStore = useAuthStore();

const isOnboardingReturn = ref(false);
const isSettingsReturn   = ref(false);
const isFreePlan         = ref(false);
const returnRoute        = ref('/tutorOnBoarding');
const failedStateKey     = ref(TUTOR_FAILED_STATE_KEY);

onMounted(() => {
    // Standalone card tokenization cancelled — no subscription involved, just redirect back
    const studentCardTokenizeRaw   = sessionStorage.getItem(STUDENT_CARD_TOKENIZE_KEY);
    const tutorCardTokenizeRaw     = sessionStorage.getItem(TUTOR_CARD_TOKENIZE_KEY);
    const instituteCardTokenizeRaw = sessionStorage.getItem(INSTITUTE_CARD_TOKENIZE_KEY);

    if (studentCardTokenizeRaw || tutorCardTokenizeRaw || instituteCardTokenizeRaw) {
        isOnboardingReturn.value = true;
        isFreePlan.value = true; // reuse "card verification cancelled, no charge" message

        if (studentCardTokenizeRaw) {
            sessionStorage.removeItem(STUDENT_CARD_TOKENIZE_KEY);
            returnRoute.value = '/studentSettings';
        } else if (tutorCardTokenizeRaw) {
            sessionStorage.removeItem(TUTOR_CARD_TOKENIZE_KEY);
            returnRoute.value = '/tutorSettings';
        } else if (instituteCardTokenizeRaw) {
            sessionStorage.removeItem(INSTITUTE_CARD_TOKENIZE_KEY);
            returnRoute.value = '/instituteSettings';
        }
        return;
    }

    // Refresh case: check saved state keys
    const tutorSaved             = sessionStorage.getItem(TUTOR_FAILED_STATE_KEY);
    const studentSaved           = sessionStorage.getItem(STUDENT_FAILED_STATE_KEY);
    const instituteSaved         = sessionStorage.getItem(INSTITUTE_FAILED_STATE_KEY);
    const tutorSettingsSaved     = sessionStorage.getItem(TUTOR_SETTINGS_FAILED_STATE_KEY);
    const studentSettingsSaved   = sessionStorage.getItem(STUDENT_SETTINGS_FAILED_STATE_KEY);
    const instituteSettingsSaved = sessionStorage.getItem(INSTITUTE_SETTINGS_FAILED_STATE_KEY);

    if (tutorSaved || studentSaved || instituteSaved || tutorSettingsSaved || studentSettingsSaved || instituteSettingsSaved) {
        isOnboardingReturn.value = true;
        if (studentSaved) {
            returnRoute.value    = '/studentOnBoarding';
            failedStateKey.value = STUDENT_FAILED_STATE_KEY;
        } else if (tutorSaved) {
            returnRoute.value    = '/tutorOnBoarding';
            failedStateKey.value = TUTOR_FAILED_STATE_KEY;
        } else if (instituteSaved) {
            returnRoute.value    = '/institutionOnBoarding';
            failedStateKey.value = INSTITUTE_FAILED_STATE_KEY;
        } else if (tutorSettingsSaved) {
            isSettingsReturn.value = true;
            returnRoute.value      = '/tutorSettings';
            failedStateKey.value   = TUTOR_SETTINGS_FAILED_STATE_KEY;
        } else if (studentSettingsSaved) {
            isSettingsReturn.value = true;
            returnRoute.value      = '/studentSettings';
            failedStateKey.value   = STUDENT_SETTINGS_FAILED_STATE_KEY;
        } else if (instituteSettingsSaved) {
            isSettingsReturn.value = true;
            returnRoute.value      = '/instituteSettings';
            failedStateKey.value   = INSTITUTE_SETTINGS_FAILED_STATE_KEY;
        }
        return;
    }

    // First landing from PayHere redirect — read all pending keys and pick the most recent one
    const tutorSettingsRaw     = sessionStorage.getItem(TUTOR_SETTINGS_PENDING_KEY);
    const studentSettingsRaw   = sessionStorage.getItem(STUDENT_SETTINGS_PENDING_KEY);
    const instituteSettingsRaw = sessionStorage.getItem(INSTITUTE_SETTINGS_PENDING_KEY);
    const studentRaw           = sessionStorage.getItem(STUDENT_PENDING_KEY);
    const tutorRaw             = sessionStorage.getItem(TUTOR_PENDING_KEY);
    const instituteRaw         = sessionStorage.getItem(INSTITUTE_PENDING_KEY);

    const getTs = (raw) => { try { return JSON.parse(raw)?.ts ?? 0; } catch { return 0; } };

    // Determine which flow is active (most recently initiated = highest ts)
    const settingsRaw = tutorSettingsRaw || studentSettingsRaw || instituteSettingsRaw;
    const onboardingRaw = studentRaw || tutorRaw || instituteRaw;

    if (!settingsRaw && !onboardingRaw) return;

    const settingsTs  = Math.max(getTs(tutorSettingsRaw), getTs(studentSettingsRaw), getTs(instituteSettingsRaw));
    const onboardingTs = Math.max(getTs(studentRaw), getTs(tutorRaw), getTs(instituteRaw));
    const useSettings = !!settingsRaw && (!onboardingRaw || settingsTs >= onboardingTs);

    if (useSettings) {
        isOnboardingReturn.value = true;
        isSettingsReturn.value   = true;
        if (tutorSettingsRaw) {
            returnRoute.value    = '/tutorSettings';
            failedStateKey.value = TUTOR_SETTINGS_FAILED_STATE_KEY;
            sessionStorage.removeItem(TUTOR_SETTINGS_PENDING_KEY);
            sessionStorage.setItem(TUTOR_SETTINGS_FAILED_STATE_KEY, '1');
        } else if (instituteSettingsRaw) {
            returnRoute.value    = '/instituteSettings';
            failedStateKey.value = INSTITUTE_SETTINGS_FAILED_STATE_KEY;
            sessionStorage.removeItem(INSTITUTE_SETTINGS_PENDING_KEY);
            sessionStorage.setItem(INSTITUTE_SETTINGS_FAILED_STATE_KEY, '1');
        } else {
            returnRoute.value    = '/studentSettings';
            failedStateKey.value = STUDENT_SETTINGS_FAILED_STATE_KEY;
            sessionStorage.removeItem(STUDENT_SETTINGS_PENDING_KEY);
            sessionStorage.setItem(STUDENT_SETTINGS_FAILED_STATE_KEY, '1');
        }
        // Also clear any stale onboarding keys so they don't interfere later
        sessionStorage.removeItem(STUDENT_PENDING_KEY);
        sessionStorage.removeItem(TUTOR_PENDING_KEY);
        sessionStorage.removeItem(INSTITUTE_PENDING_KEY);
        subscriptionApi.cancelPending().catch(() => {});
        return;
    }

    // Onboarding flow
    isOnboardingReturn.value = true;
    const raw = studentRaw || tutorRaw || instituteRaw;
    if (studentRaw) {
        returnRoute.value    = '/studentOnBoarding';
        failedStateKey.value = STUDENT_FAILED_STATE_KEY;
    } else if (tutorRaw) {
        returnRoute.value    = '/tutorOnBoarding';
        failedStateKey.value = TUTOR_FAILED_STATE_KEY;
    } else if (instituteRaw) {
        returnRoute.value    = '/institutionOnBoarding';
        failedStateKey.value = INSTITUTE_FAILED_STATE_KEY;
    }

    try {
        const pending = JSON.parse(raw);
        isFreePlan.value = pending?.pricingType === 'free';
    } catch { /* ignore */ }

    // Clear current pending keys and any stale settings keys
    if (studentRaw)   sessionStorage.removeItem(STUDENT_PENDING_KEY);
    if (tutorRaw)     sessionStorage.removeItem(TUTOR_PENDING_KEY);
    if (instituteRaw) sessionStorage.removeItem(INSTITUTE_PENDING_KEY);
    sessionStorage.removeItem(TUTOR_SETTINGS_PENDING_KEY);
    sessionStorage.removeItem(STUDENT_SETTINGS_PENDING_KEY);
    sessionStorage.removeItem(INSTITUTE_SETTINGS_PENDING_KEY);
    // Save minimal state so the correct UI persists if the user refreshes this page
    sessionStorage.setItem(failedStateKey.value, JSON.stringify({ isFreePlan: isFreePlan.value }));
    subscriptionApi.cancelPending().catch(() => {});
});

const returnToOnboarding = () => {
    sessionStorage.removeItem(failedStateKey.value);
    if (returnRoute.value === '/studentOnBoarding') {
        sessionStorage.setItem('studentOnboardingGoToStep8', '1');
    }
    router.push(returnRoute.value);
};

const goDashboard = () => {
    const role = authStore.user?.role?.toLowerCase();
    const routes = {
        student:     '/student_dashboard',
        tutor:       '/tutor_dashboard',
        institute:   '/institution_dashboard',
        institution: '/institution_dashboard',
        admin:       '/admin_dashboard',
    };
    router.push(routes[role] || '/');
};
</script>

<style scoped>
.card {
    background: var(--surface-card);
    border: 1px solid var(--surface-border);
}

.try-again-btn {
    background: #243ad7 !important;
    border-color: #243ad7 !important;
    color: #fff !important;
}

.try-again-btn:hover {
    background: #1c2fb0 !important;
    border-color: #1c2fb0 !important;
}
</style>
